import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { SHOPIFY_STOREFRONT_URL, SHOPIFY_COLLECTION_URL } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import SocialLinks from "@/components/SocialLinks";

const LINKS = [
  { name: "HOME", path: "/" },
  { name: "MUSIC", path: "/music" },
  { name: "VIDEOS", path: "/videos" },
  { name: "PASSPORT", path: "/passport" },
  { name: "SHOP", path: "/shop" },
  { name: "BIO", path: "/bio" },
];

export default function Footer() {
  const { content } = useContent();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-[#1A1A22] bg-[#0F0F14]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <LogoMark size={48} />
              <div>
                <p className="font-display font-extrabold uppercase text-2xl text-white leading-none">CHILLWOAH</p>
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#F26522] mt-1">Music • Culture • Community</p>
              </div>
            </div>
            <p className="font-editorial italic text-xl text-[#F5E6D3]">Independent Sound. Distinct Identity.</p>
            <p className="text-zinc-300">
              {content?.artist || "OKAYTWITCHY"} <span className="text-zinc-400">({content?.social_handle || "@okaytwitchy"})</span> — independent
              recording artist. XRABI is the fashion of CHILLWOAH. Brooklyn, New York.
            </p>
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-300 mb-3">Follow the world</p>
              <SocialLinks />
            </div>
          </div>

          <nav className="md:col-span-3" aria-label="Footer navigation">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#F26522] mb-5">Explore</p>
            <ul className="flex flex-col gap-1">
              {LINKS.map((l) =>
                l.path === "/shop" ? (
                  <a
                    key={l.path}
                    href={SHOPIFY_COLLECTION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-link-shop"
                    className="inline-block py-2.5 font-display font-bold uppercase text-lg text-white hover:text-[#F26522] transition-colors"
                  >
                    {l.name}
                  </a>
                ) : (
                  <Link
                    key={l.path}
                    to={l.path}
                    data-testid={`footer-link-${l.name.toLowerCase()}`}
                    className="inline-block py-2.5 font-display font-bold uppercase text-lg text-white hover:text-[#F26522] transition-colors"
                  >
                    {l.name}
                  </Link>
                )
              )}
            </ul>
          </nav>

          <div className="md:col-span-4 flex flex-col gap-6">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#F26522] mb-3">Brand hierarchy</p>
              <ul className="text-zinc-300 space-y-2">
                <li><span className="font-display font-bold text-white">CHILLWOAH</span> — the world / culture</li>
                <li><span className="font-display font-bold text-white">XRABI</span> — the fashion / product</li>
                <li><span className="font-display font-bold text-white">OKAYTWITCHY</span> — the artist / sound</li>
              </ul>
            </div>
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#F26522] mb-3">Checkout</p>
              <p className="text-zinc-300 text-sm">
                All purchases run through Shopify's secure checkout — Apple Pay, Shop Pay, cards, and supported
                cryptocurrency options appear there when enabled. No payments are processed on this site.
              </p>
            </div>
          </div>
        </div>

        <p className="font-display font-extrabold uppercase text-outline text-[16vw] md:text-[10rem] leading-none mt-16 select-none" aria-hidden="true">
          CHILLWOAH
        </p>

        <div className="mt-10 pt-6 border-t border-[#1A1A22] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-300">
            © {year} CHILLWOAH — All rights reserved • Brooklyn, NY • CHILLWOAH.com
          </p>
        </div>
      </div>
    </footer>
  );
}
